/**
 * Rich Text Formatting Helpers
 *
 * Converts between plain string format (legacy) and rich text format (array of segments)
 *
 * Rich text format (segments):
 * [
 *   { content: "Hello ", bold: false, italic: false, underline: false },
 *   { content: "world", bold: true, italic: false, underline: false },
 *   { content: "#Employee Name", variable: { fieldId, sectionId, nativeKey, ... }, ... }
 * ]
 *
 * Variable info lives on segment.variable only. Legacy (plain string + position-based tags)
 * is converted to segments on load via convertLegacyTextToRichText
 */

const DEFAULT_PROPERTIES = {
  bold: false,
  italic: false,
  underline: false,
};

const DEFAULT_SEGMENT = {
  content: '',
  ...DEFAULT_PROPERTIES,
};

/**
 * Check if text is in rich text format (array) or plain string format
 * @param {string|Array} text - Text to check
 * @returns {boolean} True if rich text format
 */
export const isRichText = (text) => Array.isArray(text) && text.length > 0 && typeof text[0] === 'object' && 'content' in text[0];

/**
 * Convert plain string to rich text format
 * @param {string} plainText - Plain text string
 * @returns {Array} Rich text array with single segment
 */
export const plainTextToRichText = (plainText) => {
  if (!plainText) {
    return [DEFAULT_SEGMENT];
  }

  if (isRichText(plainText)) {
    return plainText;
  }

  return [{
    content: plainText,
    ...DEFAULT_PROPERTIES,
  }];
};

/**
 * Check if tags use the legacy position-based format (startIndex, endIndex)
 * @param {Array} tags - Tags array
 * @returns {boolean}
 */
export const isLegacyTags = (tags) => (
  Array.isArray(tags)
  && tags.length > 0
  && typeof tags[0] === 'object'
  && 'startIndex' in tags[0]
  && 'endIndex' in tags[0]
);

/**
 * Convert legacy plain text + position-based tags into segments (with variable on each segment).
 * @param {string} plainText - Plain text string
 * @param {Array} oldTags - Legacy tags with startIndex, endIndex, fieldId, sectionId, nativeKey
 * @returns {{ segments: Array }} Segments with variable on variable segments
 */
export const convertLegacyTextToRichText = (plainText, oldTags = []) => {
  if (!plainText) {
    return { segments: [DEFAULT_SEGMENT] };
  }
  if (!Array.isArray(oldTags) || oldTags.length === 0) {
    return {
      segments: [{ content: plainText, ...DEFAULT_PROPERTIES }],
    };
  }
  const sorted = [...oldTags].sort((a, b) => a.startIndex - b.startIndex);
  const segments = [];
  let pos = 0;
  sorted.forEach((tag) => {
    const {
      startIndex,
      endIndex,
      fieldId,
      sectionId,
      nativeKey,
      colKey,
      supportsFilter,
    } = tag;
    if (startIndex > pos) {
      segments.push({
        content: plainText.slice(pos, startIndex),
        ...DEFAULT_PROPERTIES,
      });
    }
    const varContent = plainText.slice(startIndex, endIndex + 1);
    segments.push({
      content: varContent,
      ...DEFAULT_PROPERTIES,
      variable: {
        fieldId: fieldId || null,
        sectionId: sectionId || null,
        nativeKey: nativeKey || null,
        colKey: colKey || null,
        supportsFilter: supportsFilter || false,
      },
    });
    pos = endIndex + 1;
  });
  if (pos < plainText.length) {
    segments.push({
      content: plainText.slice(pos),
      ...DEFAULT_PROPERTIES,
    });
  }
  return { segments };
};

/**
 * Process a Tiptap node and add it to the segments array
 * @param {Array} segments - Array of segments
 * @param {Object} node - Tiptap node
 * @param {boolean} isFirstInParagraph - Whether this is the first node in a paragraph
 */
const processNode = (segments, node, isFirstInParagraph = false) => {
  if (!node?.type) return;

  const isBold = node.marks?.some((mark) => mark.type === 'bold') || false;
  const isItalic = node.marks?.some((mark) => mark.type === 'italic') || false;
  const isUnderline = node.marks?.some((mark) => mark.type === 'underline') || false;

  // regular text
  if (node.type === 'text') {
    const text = node.text || '';
    if (text) {
      segments.push({
        content: text,
        bold: isBold,
        italic: isItalic,
        underline: isUnderline,
      });
    }
    return;
  }

  // mention
  if (node.type === 'mention') {
    const mentionLabel = node.attrs.label || node.attrs.id || '';

    if (mentionLabel) {
      segments.push({
        content: `#${mentionLabel}`,
        bold: isBold,
        italic: isItalic,
        underline: isUnderline,
        variable: {
          fieldId: node.attrs.fieldId || null,
          sectionId: node.attrs.sectionId || null,
          nativeKey: node.attrs.nativeKey || null,
          colKey: node.attrs.colKey || null,
          supportsFilter: node.attrs.supportsFilter || false,
        },
      });

      return;
    }

    // Lone "#" (no label) stays plain text so it doesn't become a variable
    segments.push({
      content: '#',
      bold: isBold,
      italic: isItalic,
      underline: isUnderline,
    });

    return;
  }

  // hard break
  if (node.type === 'hardBreak') {
    segments.push({
      content: '\n',
      bold: false,
      italic: false,
      underline: false,
    });
    return;
  }

  // paragraph
  if (node.type === 'paragraph') {
    if (!isFirstInParagraph && segments.length > 0) {
      segments.push({
        content: '\n',
        bold: false,
        italic: false,
        underline: false,
      });
    }
    if (node.content && Array.isArray(node.content)) {
      node.content.forEach((child, index) => {
        if (child) processNode(segments, child, index === 0);
      });
    }
    return;
  }

  if (node.content && Array.isArray(node.content)) {
    node.content.forEach((child) => {
      if (child) processNode(segments, child, false);
    });
  }
};

/**
 * Convert Tiptap JSON to internal rich text format
 * @param {Object} tiptapJSON - Tiptap editor JSON
 * @returns {Array} Segments
 */
export const tiptapToRichText = (tiptapJSON) => {
  if (!tiptapJSON?.content) {
    return [DEFAULT_SEGMENT];
  }

  const segments = [];

  tiptapJSON.content.forEach((node, index) => {
    if (node) processNode(segments, node, index === 0);
  });

  // Merge consecutive non-variable segments with same formatting (never merge mention segments)
  const merged = [];
  let current = null;
  segments.forEach((segment) => {
    const sameFormat = current
      && current.bold === segment.bold
      && current.italic === segment.italic
      && current.underline === segment.underline;
    if (sameFormat && !segment.variable && !current.variable) {
      current.content += segment.content;
    } else {
      if (current) merged.push(current);
      current = { ...segment };
    }
  });

  if (current) merged.push(current);

  return merged.length > 0 ? merged : [DEFAULT_SEGMENT];
};

/**
 * Convert internal rich text format to Tiptap JSON.
 * Builds from segments only (variable info is on segment.variable); no position-based tags.
 * @param {string|Array} text - Plain string or rich text segments
 * @returns {Object} Tiptap JSON format
 */
export const richTextToTiptap = (text) => {
  const richText = plainTextToRichText(text);

  const paragraphs = [];
  let currentParagraph = [];

  const pushText = (segment, str) => {
    if (!str) return;
    const marks = [];
    if (segment.bold) marks.push({ type: 'bold' });
    if (segment.italic) marks.push({ type: 'italic' });
    if (segment.underline) marks.push({ type: 'underline' });
    currentParagraph.push({
      type: 'text',
      text: str,
      ...(marks.length > 0 ? { marks } : {}),
    });
  };

  const pushMention = (segment) => {
    const v = segment.variable || {};
    const label = (segment.content || '').replace(/^#/, '') || v.label;
    const marks = [];
    if (segment.bold) marks.push({ type: 'bold' });
    if (segment.italic) marks.push({ type: 'italic' });
    if (segment.underline) marks.push({ type: 'underline' });
    currentParagraph.push({
      type: 'mention',
      attrs: {
        id: v.fieldId || v.nativeKey || label,
        label,
        fieldId: v.fieldId ?? null,
        sectionId: v.sectionId ?? null,
        nativeKey: v.nativeKey ?? null,
        colKey: v.colKey ?? null,
        supportsFilter: v.supportsFilter ?? false,
      },
      ...(marks.length > 0 ? { marks } : {}),
    });
  };

  const newParagraph = () => {
    if (currentParagraph.length > 0) {
      paragraphs.push(currentParagraph);
      currentParagraph = [];
    } else {
      paragraphs.push([]);
    }
  };

  richText.forEach((segment) => {
    const labelFromContent = (segment.content || '').replace(/^#/, '');
    const hasVariable = segment.variable && labelFromContent.length > 0;
    if (hasVariable) {
      pushMention(segment);
      return;
    }
    const parts = segment.content.split('\n');
    parts.forEach((part, idx) => {
      if (idx > 0) newParagraph();
      pushText(segment, part);
    });
  });

  if (currentParagraph.length > 0) paragraphs.push(currentParagraph);
  if (paragraphs.length === 0) paragraphs.push([]);

  return {
    type: 'doc',
    content: paragraphs.map((content) => ({
      type: 'paragraph',
      content: content.length > 0 ? content : undefined,
    })),
  };
};
